/**
 * 2상 유동 — PRV 입구가 과냉(subcooled) 액체인 경우.
 *
 * 근거: API Standard 520 Part I, 10판(2020) Annex C.2.3
 *   입구에 응축성 증기나 비응축 가스가 없는 과냉(포화 포함) 액체에 적용한다.
 *   액체는 밸브 목(throat)의 상류 또는 목에서 플래시하며, 어느 쪽인지는
 *   "과냉 영역(subcooling region)"이 결정한다.
 *
 *   - 식 (C.30)  ωs = 9·(ρl1/ρ9 − 1)      ρ9: 포화압 Ps 의 90 % 에서의 밀도
 *   - 식 (C.32)  ηst = 2ωs / (1 + 2ωs)     전이 포화압력비
 *   - 식 (C.31)  Ps ≥ ηst·P1 → 저과냉(목 상류에서 플래시)
 *                Ps <  ηst·P1 → 고과냉(목에서 플래시)
 *   - 식 (C.39)  ηs = Ps/P1
 *   - 식 (C.36)  ηs ≤ ηst 이면 ηc = ηs
 *   - 식 (C.37)  ηs > ηst 일 때 ηc 의 정의식(음함수)
 *   - 식 (C.38)  같은 조건의 근사식
 *   - 식 (C.42)  저과냉 질량유속 (임계면 η = ηc, 아임계면 η = ηa)
 *   - 식 (C.43)  고과냉 질량유속 G = 1.414·√(ρl1·(P1 − P))
 *                (임계면 P = Ps, 아임계(전액체 유동)면 P = P2)
 *   - 식 (C.46)  인증 밸브 소요면적  A = 16.67·Q·ρl1 / (Kd·Kb·Kc·Kv·G)
 *   - 식 (C.57)  비인증 밸브 소요면적 (아래 ⚠ 주의)
 *
 * 단위: 체적유량 L/min · 밀도 kg/m³ · 압력 kPa(절대) · G kg/(s·m²) · 면적 mm²
 *   ※ Annex C 원문은 Pa 를 쓴다. 이 모듈은 kPa 로 받아 내부에서 Pa 로 환산한다.
 */
import type { ValveType } from "./gas";

/** 과냉 액체의 예비 사이징용 배출계수 (C.46 설명). */
export const KD_SUBCOOLED = 0.65;
/** 포화 액체의 예비 사이징용 배출계수 (C.46 설명). */
export const KD_SATURATED = 0.85;
/** 비인증 밸브의 배출계수 (C.57). 표준이 0.62 로 고정한다. */
export const KD_NON_CERTIFIED = 0.62;

/** 과냉 영역. */
export type SubcoolingRegion = "low" | "high";
export type SubcooledFlowRegime = "critical" | "subcritical";

interface SubcooledCommon {
  /** Q — 체적 유량 (L/min) */
  readonly volumetricFlowLpm: number;
  /** ρl1 — PRV 입구에서의 액체 밀도 (kg/m³) */
  readonly inletLiquidDensityKgM3: number;
  /** ρ9 — 포화압 Ps 의 90 % 에서 플래시한 밀도 (kg/m³) */
  readonly flashDensityKgM3: number;
  /** Ps — 입구 온도 T1 에 대응하는 포화(증기)압 (kPa 절대). 다성분계는 기포점압. */
  readonly saturationPressureKpaAbs: number;
  /** P1 — PRV 입구 압력 (kPa 절대). 설정압 + 허용 과압 + 대기압. */
  readonly relievingPressureKpaAbs: number;
  /** P2 — 총 배압 (kPa 절대) */
  readonly backpressureKpaAbs: number;
  readonly valveType: ValveType;
  /** Kd — 생략 시 과냉 0.65 / 비인증 0.62 */
  readonly dischargeCoefficient?: number;
  /** Kb — 밸런스드 벨로우즈에만 적용. 액체용은 **Figure C.3** 를 본다. */
  readonly bellowsBackpressureCorrection?: number;
  /** Kc — 파열판 없음 1.0 / 인증값 없는 조합 0.9 */
  readonly combinationCorrection?: number;
  /** Kv — 액상 점도 100 cP 이하이면 1.0 */
  readonly viscosityCorrection?: number;
  /** ηc 를 근사식 (C.38)로 구할지. 기본은 정의식 (C.37) 수치해. */
  readonly useApproximateCriticalRatio?: boolean;
}

export type SubcooledSizingInput =
  | (SubcooledCommon & { readonly certification: "certified" })
  | (SubcooledCommon & {
      readonly certification: "non-certified";
      /**
       * ⚠ 식 (C.57)의 Ps 는 **설정압(게이지)** 이다.
       * 같은 절 앞부분의 Ps(포화압, 절대)와 기호가 겹치므로 절대 섞지 말 것.
       */
      readonly setPressureKpaGauge: number;
      /** P1 게이지 — 식 (C.57)의 (P1 − P2) 항에 쓰인다. */
      readonly relievingPressureKpaGauge: number;
      /** P2 게이지 */
      readonly backpressureKpaGauge: number;
      /** Kp — 25 % 과압에서 1.0, 그 외는 Figure 39 (10 % 과압이면 0.6) */
      readonly overpressureCorrection?: number;
    });

export interface SubcooledSizingSuccess {
  readonly ok: true;
  readonly requiredAreaMm2: number;
  readonly subcoolingRegion: SubcoolingRegion;
  readonly flowRegime: SubcooledFlowRegime;
  readonly equation: "API 520 Part I (C.46)" | "API 520 Part I (C.57)";
  readonly intermediates: {
    /** ωs — 포화 액체의 오메가 파라미터, 식 (C.30) */
    readonly omegaS: number;
    /** ηst — 전이 포화압력비, 식 (C.32) */
    readonly transitionRatio: number;
    /** ηs — 포화압력비 Ps/P1, 식 (C.39) */
    readonly saturationRatio: number;
    /** ηa — 배압비 P2/P1, 식 (C.44) */
    readonly backpressureRatio: number;
    /** ηc — 임계압력비. 고과냉 영역에서는 ηs 와 같다(식 C.36). */
    readonly criticalPressureRatio: number;
    readonly criticalRatioSource:
      | "API 520 Part I (C.36)"
      | "API 520 Part I (C.37)"
      | "API 520 Part I (C.38)";
    /** G — 질량유속 (kg/s·m²) */
    readonly massFlux: number;
    readonly massFluxEquation:
      | "API 520 Part I (C.42)"
      | "API 520 Part I (C.43)";
    readonly kd: number;
    readonly kb: number;
    readonly kc: number;
    readonly kv: number;
    /** Kp — 비인증 밸브에만 존재 */
    readonly kp: number | null;
  };
  readonly warnings: readonly string[];
}

export interface SubcooledSizingFailure {
  readonly ok: false;
  readonly reason: "invalid-input" | "no-solution";
  readonly message: string;
}

export type SubcooledSizingResult =
  | SubcooledSizingSuccess
  | SubcooledSizingFailure;

/** 포화 액체 오메가 파라미터 — 식 (C.30). ωs = 9·(ρl1/ρ9 − 1) */
export function omegaSaturatedLiquid(
  inletLiquidDensity: number,
  flashDensity: number,
): number {
  return 9 * (inletLiquidDensity / flashDensity - 1);
}

/** 전이 포화압력비 — 식 (C.32). ηst = 2ωs/(1 + 2ωs) */
export function transitionSaturationRatio(omegaS: number): number {
  return (2 * omegaS) / (1 + 2 * omegaS);
}

/**
 * 식 (C.37) 의 좌변. ηs > ηst 인 저과냉 영역에서 ηc 를 정의한다.
 *
 *   [(ωs + 1/ωs − 2)/(2ηs)]·ηc² − 2(ωs−1)·ηc
 *     + ωs·ηs·ln(ηc/ηs) + (3/2)·ωs·ηs − 1 = 0
 */
export function subcooledCriticalResidual(
  omegaS: number,
  etaS: number,
  etaC: number,
): number {
  const a = (omegaS + 1 / omegaS - 2) / (2 * etaS);
  return (
    a * etaC * etaC -
    2 * (omegaS - 1) * etaC +
    omegaS * etaS * Math.log(etaC / etaS) +
    1.5 * omegaS * etaS -
    1
  );
}

/**
 * 식 (C.38) — ηc 의 근사식.
 *
 *   ηc = ηs · (2ωs/(2ωs − 1)) · [ 1 − √( 1 − (1/ηs)·((2ωs − 1)/(2ωs)) ) ]
 */
export function subcooledCriticalRatioApprox(
  omegaS: number,
  etaS: number,
): number | null {
  const twoW = 2 * omegaS;
  const inner = 1 - (1 / etaS) * ((twoW - 1) / twoW);
  if (inner < 0) return null;
  return etaS * (twoW / (twoW - 1)) * (1 - Math.sqrt(inner));
}

/**
 * 식 (C.37) 을 이분법으로 푼다.
 *
 * ηc → 0⁺ 에서 ln 항 때문에 좌변 → −∞ 이고, ηc = ηs 에서는
 * 좌변이 ηs·(0.5/ωs + 1) − 1 로 정리되는데 이는 ηs = ηst 일 때 정확히 0,
 * ηs > ηst 이면 양수다. 따라서 저과냉 영역에서는 (0, ηs) 에 근이 존재한다.
 */
export function subcooledCriticalRatioExact(
  omegaS: number,
  etaS: number,
): number | null {
  let lo = 1e-9;
  let hi = etaS;
  if (subcooledCriticalResidual(omegaS, etaS, hi) <= 0) return null;
  if (subcooledCriticalResidual(omegaS, etaS, lo) >= 0) return null;

  for (let i = 0; i < 200; i += 1) {
    const mid = (lo + hi) / 2;
    if (subcooledCriticalResidual(omegaS, etaS, mid) < 0) lo = mid;
    else hi = mid;
  }
  return (lo + hi) / 2;
}

/**
 * 저과냉 영역 질량유속 — 식 (C.42) SI.
 *
 *   G = {2(1−ηs) + 2[ωs·ηs·ln(ηs/η) − (ωs−1)(ηs−η)]}^½
 *       / ( ωs·(ηs/η − 1) + 1 ) · √(P1·ρl1)
 *
 * @param eta 임계면 ηc, 아임계면 ηa
 * @param p1Pa P1 (Pa 절대)
 */
export function lowSubcoolingMassFlux(
  omegaS: number,
  etaS: number,
  eta: number,
  p1Pa: number,
  liquidDensity: number,
): number | null {
  const inner =
    2 * (1 - etaS) +
    2 * (omegaS * etaS * Math.log(etaS / eta) - (omegaS - 1) * (etaS - eta));
  if (inner < 0) return null;
  const denominator = omegaS * (etaS / eta - 1) + 1;
  return (Math.sqrt(inner) / denominator) * Math.sqrt(p1Pa * liquidDensity);
}

/**
 * 고과냉 영역 질량유속 — 식 (C.43) SI.  G = 1.414·√(ρl1·(P1 − P))
 *
 * @param pPa 임계면 Ps, 아임계(전액체 유동)면 P2 (Pa 절대)
 */
export function highSubcoolingMassFlux(
  liquidDensity: number,
  p1Pa: number,
  pPa: number,
): number | null {
  const d = p1Pa - pPa;
  if (d < 0) return null;
  return 1.414 * Math.sqrt(liquidDensity * d);
}

function invalid(message: string): SubcooledSizingFailure {
  return { ok: false, reason: "invalid-input", message };
}

/** 과냉 액체 입구 조건의 소요 배출 면적을 계산한다 (C.2.3). */
export function sizeSubcooledLiquid(
  input: SubcooledSizingInput,
): SubcooledSizingResult {
  const {
    volumetricFlowLpm: Q,
    inletLiquidDensityKgM3: rhoL1,
    flashDensityKgM3: rho9,
    saturationPressureKpaAbs: PsKpa,
    relievingPressureKpaAbs: P1kPa,
    backpressureKpaAbs: P2kPa,
    valveType,
  } = input;

  const positives: ReadonlyArray<readonly [string, number]> = [
    ["체적 유량 Q", Q],
    ["입구 액체 밀도 ρl1", rhoL1],
    ["플래시 밀도 ρ9", rho9],
    ["포화압 Ps", PsKpa],
    ["입구 압력 P1", P1kPa],
  ];
  for (const [label, v] of positives) {
    if (!Number.isFinite(v) || v <= 0) {
      return invalid(`${label} 은(는) 0보다 큰 유한한 값이어야 합니다.`);
    }
  }
  if (!Number.isFinite(P2kPa) || P2kPa < 0) {
    return invalid("배압 P2 는 0 이상이어야 합니다.");
  }
  if (P2kPa >= P1kPa) {
    return invalid(
      "배압 P2 가 입구 압력 P1 이상입니다. 이 조건에서는 유동이 발생하지 않습니다.",
    );
  }
  if (rho9 >= rhoL1) {
    return invalid(
      "플래시 밀도 ρ9 가 입구 액체 밀도 ρl1 이상입니다. " +
        "감압하면 밀도는 작아져야 하므로 플래시 계산 결과를 확인하십시오.",
    );
  }
  if (PsKpa > P1kPa) {
    return invalid(
      "포화압 Ps 가 입구 압력 P1 보다 큽니다. 입구가 과냉 액체가 아닙니다 — " +
        "2상 입구 조건이면 Annex C.2.2 방법을 쓰십시오.",
    );
  }

  const Kc = input.combinationCorrection ?? 1.0;
  const Kv = input.viscosityCorrection ?? 1.0;
  const Kd =
    input.dischargeCoefficient ??
    (input.certification === "certified" ? KD_SUBCOOLED : KD_NON_CERTIFIED);
  for (const [label, v] of [
    ["Kd", Kd],
    ["Kc", Kc],
    ["Kv", Kv],
  ] as const) {
    if (!Number.isFinite(v) || v <= 0) return invalid(`${label} 가 올바르지 않습니다.`);
  }

  const warnings: string[] = [];

  // ── Kb: 벨로우즈에만 적용. 액체는 Figure C.3 ────────────────
  let Kb = 1.0;
  if (valveType === "balanced-bellows") {
    if (input.bellowsBackpressureCorrection === undefined) {
      warnings.push(
        "밸런스드 벨로우즈인데 배압 보정계수 Kb 가 입력되지 않아 1.0 으로 계산했습니다. " +
          "액체용은 Figure C.3(또는 제작사 자료)에서 확인해 입력해야 합니다.",
      );
    } else {
      Kb = input.bellowsBackpressureCorrection;
      if (!Number.isFinite(Kb) || Kb <= 0 || Kb > 1) {
        return invalid("Kb 는 0 초과 1 이하이어야 합니다.");
      }
    }
  } else if (input.bellowsBackpressureCorrection !== undefined) {
    warnings.push(
      "일반형·파일럿형에는 배압 보정계수 Kb 가 적용되지 않습니다. 1.0 으로 계산했습니다.",
    );
  }

  // ── Step 1~2: ωs, ηst, 과냉 영역 (식 C.30, C.32, C.31) ──────
  const omegaS = omegaSaturatedLiquid(rhoL1, rho9);
  const etaSt = transitionSaturationRatio(omegaS);
  const etaS = PsKpa / P1kPa;
  const etaA = P2kPa / P1kPa;
  const region: SubcoolingRegion = PsKpa >= etaSt * P1kPa ? "low" : "high";

  // ── Step 3: 임계 판별 + ηc ──────────────────────────────────
  let etaC: number;
  let criticalRatioSource: SubcooledSizingSuccess["intermediates"]["criticalRatioSource"];

  if (region === "high") {
    // 식 (C.36): ηs ≤ ηst 이면 ηc = ηs
    etaC = etaS;
    criticalRatioSource = "API 520 Part I (C.36)";
  } else {
    const useApprox = input.useApproximateCriticalRatio === true;
    const solved = useApprox
      ? subcooledCriticalRatioApprox(omegaS, etaS)
      : subcooledCriticalRatioExact(omegaS, etaS);
    if (solved === null || !Number.isFinite(solved) || solved <= 0) {
      return {
        ok: false,
        reason: "no-solution",
        message:
          `ωs = ${omegaS.toFixed(4)}, ηs = ${etaS.toFixed(4)} 에서 ` +
          "임계압력비를 구할 수 없습니다. 밀도·압력 입력값을 확인하십시오.",
      };
    }
    etaC = solved;
    criticalRatioSource = useApprox
      ? "API 520 Part I (C.38)"
      : "API 520 Part I (C.37)";
  }

  // 식 (C.33) 저과냉: Pcf ≥ P2 → 임계 / 식 (C.34) 고과냉: Ps ≥ P2 → 임계
  const regime: SubcooledFlowRegime =
    region === "low"
      ? etaC * P1kPa >= P2kPa
        ? "critical"
        : "subcritical"
      : PsKpa >= P2kPa
        ? "critical"
        : "subcritical";

  if (region === "high" && regime === "subcritical") {
    warnings.push(
      "고과냉 영역이면서 아임계입니다 — 밸브 전체에서 액체 유동(all-liquid flow)입니다. " +
        "5.8 의 일반 액체 사이징과 대조해 보시기 바랍니다.",
    );
  }

  // ── Step 4: 질량유속 (식 C.42 / C.43) ───────────────────────
  const P1Pa = P1kPa * 1000;
  let G: number | null;
  let massFluxEquation: SubcooledSizingSuccess["intermediates"]["massFluxEquation"];

  if (region === "low") {
    const eta = regime === "critical" ? etaC : etaA;
    G = lowSubcoolingMassFlux(omegaS, etaS, eta, P1Pa, rhoL1);
    massFluxEquation = "API 520 Part I (C.42)";
  } else {
    const pPa = (regime === "critical" ? PsKpa : P2kPa) * 1000;
    G = highSubcoolingMassFlux(rhoL1, P1Pa, pPa);
    massFluxEquation = "API 520 Part I (C.43)";
  }

  if (G === null || !Number.isFinite(G) || G <= 0) {
    return {
      ok: false,
      reason: "no-solution",
      message: "질량유속이 계산되지 않습니다. 입력 조건을 확인하십시오.",
    };
  }

  // ── Step 5: 소요 면적 (식 C.46 / C.57) ──────────────────────
  const baseArea = (16.67 * Q * rhoL1) / (Kd * Kb * Kc * Kv * G);

  if (input.certification === "certified") {
    return {
      ok: true,
      requiredAreaMm2: baseArea,
      subcoolingRegion: region,
      flowRegime: regime,
      equation: "API 520 Part I (C.46)",
      intermediates: {
        omegaS,
        transitionRatio: etaSt,
        saturationRatio: etaS,
        backpressureRatio: etaA,
        criticalPressureRatio: etaC,
        criticalRatioSource,
        massFlux: G,
        massFluxEquation,
        kd: Kd,
        kb: Kb,
        kc: Kc,
        kv: Kv,
        kp: null,
      },
      warnings,
    };
  }

  // 식 (C.57): A = 16.67·Q·ρl1·√(P1−P2) / (Kd·Kb·Kc·Kv·G·Kp·√(1.25·Ps − P2))
  // ⚠ 여기서 Ps·P1·P2 는 모두 **게이지압**이며, Ps 는 포화압이 아니라 **설정압**이다.
  const Kp = input.overpressureCorrection ?? 1.0;
  if (!Number.isFinite(Kp) || Kp <= 0 || Kp > 1) {
    return invalid("Kp 는 0 초과 1 이하이어야 합니다.");
  }
  if (input.overpressureCorrection === undefined) {
    warnings.push(
      "Kp 가 입력되지 않아 1.0(25 % 과압 기준)으로 계산했습니다. " +
        "10 % 과압이면 0.6 을 써야 합니다(5.9.2).",
    );
  }

  const numerator = input.relievingPressureKpaGauge - input.backpressureKpaGauge;
  const denominator =
    1.25 * input.setPressureKpaGauge - input.backpressureKpaGauge;
  if (!(numerator > 0) || !(denominator > 0)) {
    return invalid(
      "비인증 보정의 압력항이 0 이하입니다. 설정압·배출압·배압(모두 게이지압)을 확인하십시오.",
    );
  }

  warnings.push(
    "비인증 밸브 보정(식 C.57)은 표준서에 대조용 예제가 없어 자동 검증되지 않았습니다. " +
      "결과를 별도로 확인하시기 바랍니다.",
  );

  return {
    ok: true,
    requiredAreaMm2: (baseArea / Kp) * Math.sqrt(numerator / denominator),
    subcoolingRegion: region,
    flowRegime: regime,
    equation: "API 520 Part I (C.57)",
    intermediates: {
      omegaS,
      transitionRatio: etaSt,
      saturationRatio: etaS,
      backpressureRatio: etaA,
      criticalPressureRatio: etaC,
      criticalRatioSource,
      massFlux: G,
      massFluxEquation,
      kd: Kd,
      kb: Kb,
      kc: Kc,
      kv: Kv,
      kp: Kp,
    },
    warnings,
  };
}
